import type { AnswerCallResponse, Call, IncomingCallPayload } from '@cyphex/types/calls';
import { apiGet, apiPost } from '@/lib/api/client';
import { getRealtimeClient } from '@/lib/realtime';
import { announcedCallStatus, callIsLive } from '../lib/callState';
import type { CallsApi } from './callsAdapter';
import type { IncomingCall } from './types';

/** `call.incoming` is already shaped for this popup — it only needs flattening. */
function toIncomingCall(payload: IncomingCallPayload, now = Date.now()): IncomingCall {
  return {
    callId: payload.callId,
    kind: payload.kind === 'video' ? 'video' : 'audio',
    isGroup: payload.isGroup,
    otherParticipantCount: payload.otherCallees.length,
    ...(payload.conversationId === null ? {} : { conversationId: payload.conversationId }),
    ringingSince: now,
    ringExpiresAt: Date.parse(payload.expiresAt),
    /* THE CALLEE'S OWN SETTINGS, honoured at last. `isPriorityCaller` was
       hardcoded `false`, which made the priority-caller list — the one thing
       that is supposed to ring through Do Not Disturb — unable to ring through
       anything; `ringtone` had no path to the client at all. */
    isPriorityCaller: payload.isPriorityCaller,
    ringtone: payload.ringtone,
    ...(payload.forwardedFrom === null
      ? {}
      : { forwardedFromName: payload.forwardedFrom.displayName }),
    caller: {
      id: payload.caller.id,
      displayName: payload.caller.displayName,
      ...(payload.callerSubtitle === null ? {} : { role: payload.callerSubtitle }),
      ...(payload.caller.avatarUrl === null ? {} : { avatarUrl: payload.caller.avatarUrl }),
    },
  };
}

/** The person on the other end of a 1:1 answer — the initiator, by definition. */
const peerOf = (call: Call): { id: string; name: string } => ({
  id: call.initiator.id,
  name: call.initiator.displayName,
});

/**
 * Live incoming-call popups.
 *
 * The popup is SERVER-owned state, never a local timer: one device answering
 * emits `call.cancelled` to the others, which is what stops a phone ringing in
 * another room after you already picked up here.
 */
export const callsHttp: CallsApi = {
  accept: (callId, withVideo) =>
    apiPost<AnswerCallResponse>(`/calls/${callId}/answer`, { withVideo }).then((payload) => {
      const peer = peerOf(payload.call);
      return {
        callId,
        credentials: payload.credentials,
        kind: payload.call.kind,
        peerName: peer.name,
        peerId: peer.id,
        withVideo: withVideo && payload.call.kind === 'video',
      };
    }),

  decline: (callId, reason) =>
    apiPost<void>(`/calls/${callId}/decline`, { reason }).then(() => undefined),

  declineWithMessage: (callId, message) =>
    apiPost<void>(`/calls/${callId}/decline`, { reason: 'declined', message }).then(
      () => undefined,
    ),

  /**
   * Replay. A ring is a moment and a reload is longer than that moment, so
   * without this a refresh mid-ring silently drops the call.
   */
  listRinging: () =>
    apiGet<{ calls: IncomingCallPayload[] }>('/calls/ringing')
      .then((page) => page.calls.map((payload) => toIncomingCall(payload)))
      .catch(() => []),

  subscribe({ onRing, onCancel }) {
    const realtime = getRealtimeClient();
    realtime.connect();
    /* `user:<self>` is auto-subscribed on handshake — invites land there. */
    return realtime.onEvent(({ event }) => {
      if (event.type === 'call.incoming') {
        onRing(toIncomingCall(event.call));
        return;
      }
      /* Addressed to THIS user's other devices — "you dealt with it over
         there". Never conditional on the call: it is about the phone, not the
         conversation, and it carries no call payload to be conditional on. */
      if (event.type === 'call.cancelled') {
        onCancel(event.callId);
        return;
      }
      if (
        event.type === 'call.ended'
        || event.type === 'call.missed'
        || event.type === 'call.declined'
      ) {
        /*
         * STOP RINGING ONLY IF THERE IS NOTHING LEFT TO ANSWER.
         *
         * `call.declined` goes to the whole audience even when the call did not
         * end — one callee on a group call, or a simultaneous-ring delegate,
         * saying no. This handler took every one of the three as "the call is
         * over" and pulled the card off the screen, so a phone that was still
         * genuinely ringing went quiet while its `call_participants` row sat in
         * `ringing` until the sweeper reaped it. Nobody could answer a call
         * that was still, in the database, waiting to be answered.
         *
         * Same rule as `useCallLifecycle`, from the same file, for the same
         * reason: obey the announcement unless the server's own payload says
         * the call is still running.
         */
        if (callIsLive(announcedCallStatus(event))) return;
        onCancel(event.callId);
      }
    });
  },
};
